<script>
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeInfo } from "../toasts.js";
    import Preview from "./Preview.svelte";
    import { hostname, isAuthenticated, stack, outputFormats, outputFormat} from "../stores.js";
    import {
        selectedPreview,
        roi,
        maximumVolume,
        finalFdkSettings,
        canvasSize,
        zoom,
        offset,
        lastBeamhardCuppingPreviewImageIndex,
        lastCentershiftPreviewImageIndex
    } from "../previewstores.js";
    import {
        formatJson,
        checkForErrorsInJson,
        extractPhaseRetrievalBaseFromPath,
        runReconstructionApi,
        runReconstructionDagApi,
        isNumeric,
        now,
    } from "../utils.js";
    import Modal from "./Modal.svelte";
    import { onDestroy } from "svelte";
    import { pipelineObserver } from '../pipelinestores.js';
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';
    import { user } from "../auth.js";

    export let params;

    export let sliceIndex;
    export let finalBeamhardCuppingOrCentershiftSlicePreviews;
    let beamhardCuppingOrCentershiftPreviewImageIndex;

    let canvasWidth = $canvasSize;
    let previewOverlayCanvas;
    let clicked = false;
    let drawStatus = false;
    
    // min && max
    $: validMinSlices =
        $finalFdkSettings.min_slice >= $roi.top && $finalFdkSettings.min_slice <= $finalFdkSettings.max_slice;
    $: validMaxSlices =
        $finalFdkSettings.max_slice >= $finalFdkSettings.min_slice && $finalFdkSettings.max_slice <= $roi.bot;
    $: validLeftRoi =
        $finalFdkSettings.slice_roi.left >= $roi.left &&
        $finalFdkSettings.slice_roi.left <= $finalFdkSettings.slice_roi.right;
    $: validRigthtRoi =
        $finalFdkSettings.slice_roi.right > $finalFdkSettings.slice_roi.left &&
        $finalFdkSettings.slice_roi.right <= $roi.right;
    $: validTopRoi =
        $finalFdkSettings.slice_roi.top >= $roi.left &&
        $finalFdkSettings.slice_roi.top < $finalFdkSettings.slice_roi.bot;
    $: validBottomRoi =
        $finalFdkSettings.slice_roi.bot > $finalFdkSettings.slice_roi.top &&
        $finalFdkSettings.slice_roi.bot <= $roi.right;
    $: validAngle = $finalFdkSettings.angle_offset >= -360 && $finalFdkSettings.angle_offset <= 360;
    
    $: if (outputFormats.find(f => f.id == $finalFdkSettings?.output_format?.id)) {
        $outputFormat = $finalFdkSettings.output_format;
        console.log('CHANGED OUTPUT FORMAT TO: ', $finalFdkSettings?.output_format?.id);
    }

    $: initPreview($selectedPreview);
    $: onPreviewManipulationElementLoaded(previewOverlayCanvas);
    $: onSliceRoiUpdated($zoom, $offset);

    function onSliceRoiUpdated() {
        currentRectX0 = ~~(
            ($finalFdkSettings.slice_roi.left * (canvasWidth * $zoom)) / $maximumVolume.slice_roi.right +
            $offset.x
        );
        currentRectY0 = ~~(
            ($finalFdkSettings.slice_roi.top * (canvasWidth * $zoom)) / $maximumVolume.slice_roi.right +
            $offset.y
        );
        currentRectX1 = ~~(
            ($finalFdkSettings.slice_roi.right * (canvasWidth * $zoom)) / $maximumVolume.slice_roi.right +
            $offset.x
        );
        currentRectY1 = ~~(
            ($finalFdkSettings.slice_roi.bot * (canvasWidth * $zoom)) / $maximumVolume.slice_roi.right +
            $offset.y
        );
        console.log(`CURRENT RECT ${currentRectX0} ${currentRectY0} ${currentRectX1} ${currentRectY1}`);
        clearCrossLines();
        drawRectangle(undefined, undefined, "blue");
    }

    let octx;
    async function onFinalPreviewImageMouseMove(ev) {
        // Make sure the drawing context is initialised
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            octx = previewOverlayCanvas.getContext("2d");
        }

        // Clear overlay canvas and draw moving line over image as well as existing selected line
        if (octx) {
            clearCrossLines();
            drawRectangle(undefined, undefined, "blue");
            if (currentRectMode == "DRAW") drawRectangle(ev.offsetX, ev.offsetY, "yellow");
        }
    }

    async function clearCrossLines() {
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            if (previewOverlayCanvas === null) {
                return;
            }
            octx = previewOverlayCanvas.getContext("2d");
        }
        if (octx) {
            octx.clearRect(0, 0, previewOverlayCanvas.width, previewOverlayCanvas.height);
        }
    }

    let currentRectMode = "MOVE";
    let currentRectX0 = 0;
    let currentRectY0 = 0;
    let currentRectX1 = 0;
    let currentRectY1 = 0;
    async function drawRectangle(x, y, color) {
        if (!previewOverlayCanvas) {
            previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
            if (previewOverlayCanvas === null) {
                return;
            }
            octx = previewOverlayCanvas.getContext("2d");
        }
        // Draws a rectangle
        if (octx) {
            if (x == undefined || y == undefined) {
                // If no new x, y, just draw the current rectangle
                octx.beginPath();
                octx.rect(currentRectX0, currentRectY0, currentRectX1 - currentRectX0, currentRectY1 - currentRectY0);
                octx.strokeStyle = color;
                octx.stroke();
            }
            if (y >= 0 && y < canvasWidth && x >= 0 && x < canvasWidth) {
                octx.beginPath();
                octx.rect(currentRectX0, currentRectY0, x - currentRectX0, y - currentRectY0);
                octx.strokeStyle = color;
                octx.stroke();
            }
        }
    }

    async function onClickFinalPreviewImage(ev) {
        if (drawStatus === false) {
            return;
        }
        let x = ev.offsetX;
        let y = ev.offsetY;
        console.log(`X=${x} Y=${y}`);
        //console.log(`UPDATED RECT: ${currentRectMode} ${currentRectX0} ${currentRectY0} ${currentRectX1} ${currentRectY1}`);

        clearCrossLines();
        if (currentRectMode == "MOVE") {
            currentRectX0 = x;
            currentRectY0 = y;
            currentRectX1 = x;
            currentRectY1 = y;
            currentRectMode = "DRAW";
        } else {
            if (currentRectX0 > x) {
                currentRectX1 = currentRectX0;
                currentRectX0 = x;
            } else {
                currentRectX1 = x;
            }
            if (currentRectY0 > y) {
                currentRectY1 = currentRectY0;
                currentRectY0 = y;
            } else {
                currentRectY1 = y;
            }
            drawRectangle(currentRectX1, currentRectY1, "blue");
            currentRectMode = "MOVE";
            $finalFdkSettings.slice_roi.left = ~~(
                ((currentRectX0 - $offset.x) * $maximumVolume.slice_roi.right) /
                (canvasWidth * $zoom)
            );
            $finalFdkSettings.slice_roi.top = ~~(
                ((currentRectY0 - $offset.y) * $maximumVolume.slice_roi.right) /
                (canvasWidth * $zoom)
            );
            $finalFdkSettings.slice_roi.right = ~~(
                ((currentRectX1 - $offset.x) * $maximumVolume.slice_roi.right) /
                (canvasWidth * $zoom)
            );
            $finalFdkSettings.slice_roi.bot = ~~(
                ((currentRectY1 - $offset.y) * $maximumVolume.slice_roi.right) /
                (canvasWidth * $zoom)
            );
        }
    }

    let showModal = false;
    let fdkDagSettings = {};
    function startReconstructionDag(ev) {
        console.log("STARTING RECONSTRUCTION...");
        console.log("fdkDagSettings = ", fdkDagSettings);
        console.log("$finalFdkSettings = ", $finalFdkSettings);

        ev.stopPropagation();

        // Use the saved values from finalFdkSettings
        console.log(`STARTING RECONSTRUCTION FOR ${params.session}/${params.tomography}`);
        console.log($selectedPreview);

        // Temporarily set streaks settings to 0 since we don't have support for this yet
        $finalFdkSettings.beamhard_streaks_factor = 0; // TODO
        $finalFdkSettings.beamhard_streaks_threshold = 0; // TODO

        // Copy generic DAG settings from the current preview as base, override from final fdk settings and delete unused/irrelevant settings
        if ($selectedPreview) {
            fdkDagSettings.generic_settings = {
                ...$selectedPreview?.preview_input?.generic_preview_input,
            };
            fdkDagSettings.generic_settings.reconstruction_settings.centershift_object_mm =
                $finalFdkSettings.centershift_object_mm;
            fdkDagSettings.generic_settings.reconstruction_settings.tilt_deg = $finalFdkSettings.tilt_deg;
            fdkDagSettings.generic_settings.reconstruction_settings.beamhard_cupping =
                $finalFdkSettings.beamhard_cupping;

            delete fdkDagSettings.generic_settings.calibration_id;
            delete fdkDagSettings.generic_settings.slices;
            delete fdkDagSettings.generic_settings.reconstruction_settings?.tilt_is_optimized;
            delete fdkDagSettings.generic_settings.reconstruction_settings?.centershift_is_optimized;
        }

        // Add streaks and angle offset settings
        if (fdkDagSettings.generic_settings?.reconstruction_settings) {
            fdkDagSettings.generic_settings.reconstruction_settings.beamhard_streaks_factor =
                $finalFdkSettings.beamhard_streaks_factor;
            fdkDagSettings.generic_settings.reconstruction_settings.beamhard_streaks_threshold =
                $finalFdkSettings.beamhard_streaks_threshold;
            fdkDagSettings.generic_settings.reconstruction_settings.angle_offset = $finalFdkSettings.angle_offset;
        }

        // Add the task-specific settings
        fdkDagSettings.volume = {
            min_slice: $finalFdkSettings.min_slice,
            max_slice: $finalFdkSettings.max_slice,
            slice_roi: { ...$finalFdkSettings.slice_roi },
        };

        // Copy the selected output format
        fdkDagSettings.output_format =  $finalFdkSettings.output_format.file_format;
        fdkDagSettings.zip_binaries =  $finalFdkSettings.output_format.zip_binaries;


        // Format the settings for use with the datapipeline API
        const fdkInput = getFdkDagInput(fdkDagSettings);
        console.log("FDK INPUT: ", fdkInput);

        // Show the confirm dialog
        showModal = true;
    }

    async function onConfirmFdkSettings() {
        console.log("OK, CALLING RECONTRUCTION PIPELINE...");
        const fdkInput = getFdkDagInput(fdkDagSettings);
        //const runReconstructionApiResponse = await runReconstructionApi($hostname, $isAuthenticated, $stack, params.session, params.tomography, fdkInput);
        const runReconstructionApiResponse = await runReconstructionDagApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography,
            fdkInput
        );

        if (isNumeric(runReconstructionApiResponse)) {
            let msg = `${now()}<br/>Run reconstruction API error: ${runReconstructionApiResponse}`;
            toast.push(msg, toastThemeError);
            return;
        }

        // The API should respond with a fully qualified pipeline name
        let pipelineId = runReconstructionApiResponse?.id;

        // Indicate to the user that a new reconstruction job has been started
        let msg = `${now()}<br/>`;
        msg += `Started reconstruction at slices ${$finalFdkSettings?.min_slice} to ${$finalFdkSettings?.max_slice}
            with centershift ${$finalFdkSettings?.centershift_object_mm} mm`;
        const beamhardCupping = $finalFdkSettings?.beamhard_cupping;
        if (!isNaN(beamhardCupping)) {
            msg += ` and beamhard cupping ${beamhardCupping}`;
        }
        if ($finalFdkSettings?.output_format) {
            msg += ` <br/>Output as ${$finalFdkSettings?.output_format?.label}`;
        }
        if (pipelineId) {
            msg += ` <br/>(${pipelineId})`;
        }
        toast.push(msg, toastThemeInfo);

        // Add the new pipeline to the pipeline observables list
        $pipelineObserver.addPipelineToObserve(pipelineId);
    }

    function drawKey(event) {
        if (event.keyCode === 68) drawStatus = true;
        //cursor: crosshair
    }

    function getFdkDagInput(fdkSettings) {
        const fdkInput = {
            ...fdkSettings,
        };
        return fdkInput;
    }

    let showJson = false;
    function onShowJson() {
        showJson = true;
    }

    // This should be called whenever the selectedPreview changes
    function initPreview($selectedPreview) {
        if ($selectedPreview) {
            console.log("++++++++ INIT FINAL PREVIEW -- ", $selectedPreview);
            previewOverlayCanvas?.addEventListener("click", onClickFinalPreviewImage);
            previewOverlayCanvas?.addEventListener("contextmenu", (e) => {
                e.preventDefault();
            });
            document.addEventListener("keydown", drawKey);

            if ($selectedPreview?.preview_input?.preview_input?.preview_type == "BEAMHARD_CUPPING") {
                // If we have a beamhard cupping value, set the corresponding image index
                if (isNumeric($finalFdkSettings?.beamhard_cupping)) {
                    beamhardCuppingOrCentershiftPreviewImageIndex = Math.round($finalFdkSettings?.beamhard_cupping * 63);
                    console.log('FINAL PREVIEW FOUND IMAGE INDEX: ', $finalFdkSettings?.beamhard_cupping, ' -> ', beamhardCuppingOrCentershiftPreviewImageIndex);
                } else {
                    beamhardCuppingOrCentershiftPreviewImageIndex = isNumeric($lastBeamhardCuppingPreviewImageIndex) ? $lastBeamhardCuppingPreviewImageIndex : 0;
                }
            } else {
                beamhardCuppingOrCentershiftPreviewImageIndex = isNumeric($lastCentershiftPreviewImageIndex) ? $lastCentershiftPreviewImageIndex : undefined;
            }
        }

        if ($finalFdkSettings) {
            $finalFdkSettings.output_format = $outputFormat || outputFormats[0];
        }
    }

    function onPreviewManipulationElementLoaded(previewOverlayCanvas) {
        if (previewOverlayCanvas && previewOverlayCanvas.getAttribute("listener")) {
            return;
        } else {
            previewOverlayCanvas?.addEventListener("click", onClickFinalPreviewImage);
            previewOverlayCanvas?.addEventListener("mousemove", onFinalPreviewImageMouseMove);
            octx = previewOverlayCanvas?.getContext("2d");
        }
    }

    onDestroy(() => {
        previewOverlayCanvas.removeEventListener("click", onClickFinalPreviewImage);
        previewOverlayCanvas.removeEventListener("click", onFinalPreviewImageMouseMove);
        document.removeEventListener("keydown", drawKey);
    });
</script>

<Preview 
    headerText="Final preview" 
    type="FINAL"
    {sliceIndex} 
    availableSlicePreviews={finalBeamhardCuppingOrCentershiftSlicePreviews}
    previewImageIndex={beamhardCuppingOrCentershiftPreviewImageIndex} 
    bind:drawStatus 
    bind:previewOverlayCanvas>
    <final-advanced>
        <form class="final-form">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Slices</label>
            <input
                class:inValid={clicked && !validMinSlices}
                type="number"
                bind:value={$finalFdkSettings.min_slice}
                min={$roi.top}
                max={$roi.bot && $finalFdkSettings.max_slice} />
            to
            <input
                class:inValid={clicked && !validMaxSlices}
                type="number"
                bind:value={$finalFdkSettings.max_slice}
                min={$roi.top && $finalFdkSettings.max_slice}
                max={$roi.bot} />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Left</label>
            <input
                class:inValid={clicked && !validLeftRoi}
                type="number"
                bind:value={$finalFdkSettings.slice_roi.left}
                min={$maximumVolume.slice_roi.left}
                max={$maximumVolume.slice_roi.right}
                on:input={onSliceRoiUpdated} />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Right</label>
            <input
                class:inValid={clicked && !validRigthtRoi}
                type="number"
                bind:value={$finalFdkSettings.slice_roi.right}
                min={$maximumVolume.slice_roi.left}
                max={$maximumVolume.slice_roi.right}
                on:input={onSliceRoiUpdated} />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Top</label>
            <input
                class:inValid={clicked && !validTopRoi}
                type="number"
                bind:value={$finalFdkSettings.slice_roi.top}
                min={$maximumVolume.slice_roi.top}
                max={$maximumVolume.slice_roi.bot}
                on:input={onSliceRoiUpdated} />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Bottom</label>
            <input
                class:inValid={clicked && !validBottomRoi}
                type="number"
                bind:value={$finalFdkSettings.slice_roi.bot}
                min={$maximumVolume.slice_roi.top}
                max={$maximumVolume.slice_roi.bot}
                on:input={onSliceRoiUpdated} />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Angle</label>
            <input
                class:inValid={clicked && !validAngle}
                type="number"
                bind:value={$finalFdkSettings.angle_offset}
                min="-360"
                max="360" />
            degrees
            <div>&nbsp;</div>
        </form>
        <form class="output-format-form">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label>Output:</label>
            {#each outputFormats as outputFormat}
                <label use:tooltip={{content: `${outputFormat?.description}`, placement: "top", delay: [1000, 0]}}>
                    <input
                        type="radio"
                        name="reconstructionOutputFormat"
                        bind:group={$finalFdkSettings.output_format}
                        value={outputFormat}
                    />

                    {outputFormat.label}
                </label>
            {/each}
            <span/><!--Dummy element to fill out the grid with purpose of aligning tooltip-->
        </form>

    </final-advanced>
    <button on:click|preventDefault|stopPropagation={startReconstructionDag}>Start reconstruction</button>
    {#if showModal}
        <Modal
            on:close={() => {
                showJson = false;
                showModal = false;
            }}
            on:ok={() => {
                showModal = false;
                onConfirmFdkSettings();
                showJson = false;
            }}
            on:json={onShowJson}
            jsonable={true}
            dataError={checkForErrorsInJson(getFdkDagInput(fdkDagSettings))}>
            <h2 slot="header">Confirm reconstruction settings</h2>

            {#if showJson}
                <pre>{formatJson(getFdkDagInput(fdkDagSettings))}</pre>
            {:else}
                <h3>Preprocessing settings</h3>
                <settings>
                    delta: {fdkDagSettings?.generic_settings?.phase_retrieval_settings?.phase_retrieval?.delta?.toExponential()}
                    <br />
                    mu: {fdkDagSettings?.generic_settings?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(
                        3
                    )}
                    <br />
                </settings>
                <h3>Reconstruction settings</h3>
                <settings>
                    centershift: {fdkDagSettings?.generic_settings?.reconstruction_settings?.centershift_object_mm} mm
                    <br />
                    {#if isNumeric(fdkDagSettings?.generic_settings?.reconstruction_settings?.tilt_deg)}
                        tilt: {fdkDagSettings?.generic_settings?.reconstruction_settings?.tilt_deg} °
                        <br />
                    {/if}
                    slices: {fdkDagSettings?.volume?.min_slice}..{fdkDagSettings?.volume?.max_slice}
                    <br />
                    slice roi:
                    <br />
                    &emsp; left: {fdkDagSettings?.volume?.slice_roi?.left}
                    <br />
                    &emsp; right: {fdkDagSettings?.volume?.slice_roi?.right}
                    <br />
                    &emsp; top: {fdkDagSettings?.volume?.slice_roi?.top}
                    <br />
                    &emsp; bottom: {fdkDagSettings?.volume?.slice_roi?.bot}
                    <br />
                    angle: {fdkDagSettings?.generic_settings?.reconstruction_settings?.angle_offset} °
                    <br />
                    localized: {fdkDagSettings?.generic_settings?.reconstruction_settings?.is_localized}
                    <br />
                    offset: {fdkDagSettings?.generic_settings?.reconstruction_settings?.is_offset_scan}
                    <br />
                    beamhard cupping factor: {fdkDagSettings?.generic_settings?.reconstruction_settings
                        ?.beamhard_cupping}
                    <br />
                    output format: {fdkDagSettings?.output_format}
                    <br>
                    zip archive: {fdkDagSettings?.zip_binaries}
                    <br>
                    <p>
                        {#if fdkDagSettings?.output_format == 'tiff_slices' && fdkDagSettings?.zip_binaries == false}
                            <span style="color: red">Please note: The copy command for Raw TIFF slices will only be availabe for Admin users.</span>  
                        {/if}
                    </p>
                </settings>
            {/if}
        </Modal>
    {/if}
</Preview>

<style>
    final-advanced {
        display: block;
        margin-bottom: 16px;
    }

    .final-form {
        display: grid;
        grid-template-columns: 15% 20% 15% 20%;
        grid-column-gap: 2.5%;
        align-items: baseline;
    }

    .output-format-form {
        display: grid;
        grid-template-columns: 15% auto auto auto 1fr;
        grid-column-gap: 2.5%;
        align-items: baseline;
    }

    .inValid {
        outline: solid 2px red;
    }

    settings {
        font-family: monospace;
    }
</style>
